<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';

export default {
  components: {
    LoadingState,
    SettingsFieldSection,
    NextButton,
    SelectInput,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedAgentBotId: null,
      botConversationsStartPending: true,
    };
  },
  computed: {
    ...mapGetters({
      agentBots: 'agentBots/getBots',
      uiFlags: 'agentBots/getUIFlags',
      accountId: 'getCurrentAccountId',
    }),
    currentInboxId() {
      return this.inbox?.id || this.$route.params.inboxId;
    },
    activeAgentBot() {
      return this.$store.getters['agentBots/getActiveAgentBot'](
        this.currentInboxId
      );
    },
    agentBotConfig() {
      return this.$store.getters['agentBots/getAgentBotConfig'](this.inbox.id);
    },
  },
  watch: {
    activeAgentBot() {
      this.selectedAgentBotId = this.activeAgentBot.id;
    },
    agentBotConfig: {
      handler(newConfig) {
        this.botConversationsStartPending =
          newConfig.botConversationsStartPending;
      },
      immediate: true,
    },
  },
  mounted() {
    this.fetchBotData();
  },

  methods: {
    fetchBotData() {
      this.$store.dispatch('agentBots/get');
      this.$store.dispatch('agentBots/fetchAgentBotInbox', this.currentInboxId);
    },
    async updateActiveAgentBot() {
      try {
        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.inbox.id,
          // Added this to make sure that empty values are not sent to the API
          botId: this.selectedAgentBotId ? this.selectedAgentBotId : undefined,
          botConversationsStartPending: this.botConversationsStartPending,
        });
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE'));
      }
    },
    editBotRules() {
      window.open(
        `/bot-editor/bots/by-inbox/${this.accountId}/${this.inbox.id}`,
        '_blank'
      );
    },
    async disconnectBot() {
      try {
        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.inbox.id,
        });
        useAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="mx-6 max-w-4xl">
    <LoadingState v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />
    <form v-else @submit.prevent="updateActiveAgentBot">
      <SettingsFieldSection
        :label="$t('AGENT_BOTS.BOT_CONFIGURATION.TITLE')"
        :help-text="$t('AGENT_BOTS.BOT_CONFIGURATION.DESC')"
        class="[&>div]:!items-start"
      >
        <SelectInput
          v-model="selectedAgentBotId"
          :placeholder="$t('AGENT_BOTS.BOT_CONFIGURATION.SELECT_PLACEHOLDER')"
          :options="agentBots.map(bot => ({ value: bot.id, label: bot.name }))"
        />
        <template #extra>
          <div class="grid grid-cols-1 lg:grid-cols-8 mt-3">
            <div class="col-span-1 lg:col-span-2 invisible" />
            <div class="col-span-1 lg:col-span-6 mx-1">
              <div v-if="selectedAgentBotId" class="mb-3">
                <label class="flex items-center gap-2">
                  <input
                    v-model="botConversationsStartPending"
                    type="checkbox"
                    class="rounded"
                  />
                  <span>{{
                    $t(
                      'AGENT_BOTS.BOT_CONFIGURATION.START_CONVERSATIONS_AS_PENDING'
                    )
                  }}</span>
                </label>
              </div>
              <div class="flex gap-2">
                <NextButton
                  type="submit"
                  :label="$t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT')"
                  :is-loading="uiFlags.isSettingAgentBot"
                />
                <NextButton
                  v-if="selectedAgentBotId"
                  type="button"
                  faded
                  :label="$t('AGENT_BOTS.BOT_CONFIGURATION.EDIT_BOT_RULES')"
                  @click="editBotRules"
                />
                <NextButton
                  type="button"
                  :disabled="!selectedAgentBotId"
                  :is-loading="uiFlags.isDisconnecting"
                  faded
                  ruby
                  @click="disconnectBot"
                >
                  {{ $t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECT') }}
                </NextButton>
              </div>
            </div>
          </div>
        </template>
      </SettingsFieldSection>
    </form>
  </div>
</template>
